import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Package, ChevronRight, Clock, CheckCircle, 
  Truck, XCircle, AlertCircle
} from 'lucide-react';

const OrdersPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const res = await api().get('/orders');
        setOrders(res.data);
      } catch (err) {
        console.error('Error loading orders:', err);
      }
      setLoading(false);
    };
    loadOrders();
  }, [api]);

  const statusConfig = {
    pending: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
    preparing: { label: 'Em Preparação', color: 'bg-blue-100 text-blue-700', icon: Package },
    ready: { label: 'Pronto', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    in_delivery: { label: 'Em Entrega', color: 'bg-purple-100 text-purple-700', icon: Truck },
    completed: { label: 'Concluído', color: 'bg-green-100 text-green-700', icon: CheckCircle },
    cancelled: { label: 'Cancelado', color: 'bg-red-100 text-red-700', icon: XCircle },
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-AO', { 
      day: '2-digit', 
      month: 'short', 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  return (
    <div className="min-h-screen bg-zinc-100 pb-20">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-zinc-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate(-1)} 
            className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-zinc-100"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="font-barlow font-bold text-xl">Meus Pedidos</h1>
        </div>
      </div>

      {/* Orders List */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl h-32 animate-pulse"></div>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-zinc-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="w-10 h-10 text-zinc-300" />
            </div>
            <h2 className="font-barlow font-bold text-xl text-zinc-800 mb-2">Sem Pedidos</h2>
            <p className="text-zinc-500 font-manrope mb-6">
              Ainda não fez nenhum pedido.
            </p>
            <Link to="/catalog">
              <button className="px-6 py-3 bg-sonangol-red text-white rounded-xl font-barlow font-bold" data-testid="browse-btn">
                Explorar Produtos
              </button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.map((order) => {
              const status = statusConfig[order.status] || statusConfig.pending;
              const StatusIcon = status.icon;
              return (
                <Link 
                  key={order.id} 
                  to={`/order/${order.id}`}
                  data-testid={`order-item-${order.id}`}
                >
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-zinc-100 active-scale">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-barlow font-bold text-zinc-800">
                          Pedido #{order.id.slice(0, 8)}
                        </p>
                        <p className="text-xs text-zinc-500 font-manrope">
                          {formatDate(order.created_at)}
                        </p>
                      </div>
                      <Badge className={`${status.color} border-0`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {status.label}
                      </Badge>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-zinc-600 font-manrope">
                          {order.items.length} {order.items.length === 1 ? 'produto' : 'produtos'}
                        </p>
                        <p className="font-barlow font-bold text-sonangol-red">
                          {order.total_aoa.toLocaleString('pt-AO')} AOA
                        </p>
                      </div>
                      <ChevronRight className="w-5 h-5 text-zinc-400" />
                    </div>
                    
                    <div className="mt-3 pt-3 border-t border-zinc-100 flex items-center gap-2 text-xs text-zinc-500">
                      {order.delivery_method === 'pickup' ? (
                        <Package className="w-4 h-4" />
                      ) : (
                        <Truck className="w-4 h-4" />
                      )}
                      <span>{order.station_name}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default OrdersPage;
